// COST-01: estimated vs actual on the job. Shown only with costing.view;
// entry, delete and "take estimate" only with costing.manage.
import './jobCosting.css'
import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { useApiData } from '@/hooks/useApi'
import { useCostingPermission } from './fixturePermissions'
import { Button } from '@/components/ui/Button'
import { formatMoney } from '@/lib/format'
import { resolveApiUrl } from '@/lib/apiUrl'
import { toast } from '@/lib/toast'
import { JobProfitBar } from './JobProfitBar'
import { CostEntryForm } from './CostEntryForm'
import { COST_TYPE_LABEL, costingApi, designMode, type CostingSettings, type JobCosting, type LaborRateRow, tradeTargetFor } from './api'

const heading = { fontSize: 11, fontWeight: 600, color: 'var(--midnight-text3)', textTransform: 'uppercase' as const, letterSpacing: '0.18em', marginBottom: 8 }
const cell = { padding: '6px 4px', fontSize: 13 }

function variance(p: number | null) {
  if (p == null) return '—'
  return `${p > 0 ? '+' : ''}${p}%`
}

export function JobCostingPanel({ jobId }: { jobId: string }) {
  const canView = useCostingPermission('costing.view')
  if (!canView) return null
  return <JobCostingPanelInner key={`${jobId}:${designMode()}`} jobId={jobId} />
}

function JobCostingPanelInner({ jobId }: { jobId: string }) {
  const demo = designMode()
  const canManage = useCostingPermission('costing.manage')
  const [adding, setAdding] = useState(false)
  const q = useApiData<JobCosting>(() => costingApi.job(jobId), [jobId])
  const people = useApiData<LaborRateRow[]>(() => (canManage ? costingApi.laborRates() : Promise.resolve([])), [canManage])
  const settings = useApiData<CostingSettings>(() => costingApi.settings(), [])
  const data = q.data
  const s = settings.data
  // A trade's green target replaces the workspace green line for this job.
  const thresholds = s ? { redBelow: s.redBelow, greenAt: tradeTargetFor(s, data?.trade?.name) ?? s.greenAt } : undefined

  async function takeEstimate() {
    try { await costingApi.snapshot(jobId); toast.success(demo ? 'Demo estimate updated temporarily' : 'Estimate taken from the quote'); q.refetch() }
    catch (err) { toast.error(err, 'take the estimate') }
  }
  async function remove(entryId: string) {
    try { await costingApi.deleteEntry(jobId, entryId); toast.success(demo ? 'Demo cost removed temporarily' : 'Cost removed'); q.refetch() }
    catch (err) { toast.error(err, 'remove the cost') }
  }

  return (
    <section className="job-costing" aria-label="Job costing" data-testid="job-costing-panel">
      <p style={heading}>Job costing</p>
      {q.loading && !data ? (
        <div role="status" aria-label="Loading job costing" className="h-10 shimmer rounded" />
      ) : q.error ? (
        <div role="alert" style={{ fontSize: 13, color: 'var(--blush-text)' }}>
          <p>Job costing is unavailable. {q.error}</p>
          <Button size="sm" variant="secondary" onClick={q.refetch}>Retry job costing</Button>
        </div>
      ) : data ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <JobProfitBar
            price={data.price}
            estimatedCost={data.totals.estimatedCost}
            actualCost={data.totals.actualCost}
            marginPercent={data.totals.marginPercent}
            estimatedMarginPercent={data.totals.estimatedMarginPercent}
            thresholds={thresholds}
          />

          {!data.estimateTakenAt && (
            <div style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>
              No estimate yet for this job.
              {canManage && <> <Button size="sm" variant="secondary" needsNetwork onClick={takeEstimate}>Take estimate from quote</Button></>}
            </div>
          )}

          <table className="job-costing-comparison" aria-label="Estimated vs actual" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--midnight-text3)', fontSize: 12 }}>
                <th style={cell}>Item</th><th style={cell}>Estimated</th><th style={cell}>Actual</th><th style={cell}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.id}>
                  <td style={cell}>{l.description}<div style={{ fontSize: 11, color: 'var(--midnight-text3)' }}>{COST_TYPE_LABEL[l.costType]}{l.estimatedHours != null ? ` · Actual ${l.actualHours} h · Estimated ${l.estimatedHours} h` : ''}</div></td>
                  <td data-label="Estimated" style={cell}>{formatMoney(l.estimatedCost)}</td>
                  <td data-label="Actual" style={cell}>{formatMoney(l.actualCost)}</td>
                  <td data-label="Variance" style={cell}>{variance(l.variancePercent)}</td>
                </tr>
              ))}
              {data.unassignedActualCost !== 0 && (
                <tr><td style={cell}>Not tied to a line</td><td data-label="Estimated" style={cell}>—</td><td data-label="Actual" style={cell}>{formatMoney(data.unassignedActualCost)}</td><td data-label="Variance" style={cell}>—</td></tr>
              )}
              <tr style={{ fontWeight: 600, borderTop: '0.5px solid rgb(var(--fg-rgb) / 0.12)' }}>
                <td style={cell}>Total</td>
                <td data-label="Estimated" style={cell}>{formatMoney(data.totals.estimatedCost)}</td>
                <td data-label="Actual" style={cell}>{formatMoney(data.totals.actualCost)}</td>
                <td data-label="Variance" style={cell}>{variance(data.totals.variancePercent)}</td>
              </tr>
            </tbody>
          </table>

          <ul aria-label="By cost type" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--midnight-text3)' }}>
            {data.byCostType.filter((t) => t.estimatedCost || t.actualCost).map((t) => (
              <li key={t.costType}>{COST_TYPE_LABEL[t.costType]}: Actual {formatMoney(t.actualCost)} · Estimated {formatMoney(t.estimatedCost)}</li>
            ))}
          </ul>

          <div>
            <p style={heading}>Costs posted</p>
            {data.entries.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>No labour or expenses yet.</p>
            ) : (
              <ul aria-label="Cost entries" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.entries.map((e) => (
                  <li key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 13 }}>
                    <span>
                      {e.kind === 'LABOR' ? `Labour · ${e.hours} h @ ${formatMoney(e.rate)}` : `${COST_TYPE_LABEL[e.costType]}${e.vendor ? ` · ${e.vendor}` : ''}`}
                      {e.reimbursable ? ' · reimbursable' : ''}
                      {e.description ? ` · ${e.description}` : ''}
                      {e.receiptUrl && <> · <a href={resolveApiUrl(e.receiptUrl)} target="_blank" rel="noopener noreferrer">Receipt</a></>}
                    </span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {formatMoney(e.amount)}
                      {canManage && !e.purchaseReceiptId && (
                        <Button size="sm" variant="ghost" aria-label="Remove cost" needsNetwork leadingIcon={<Trash2 size={13} />} onClick={() => remove(e.id)} />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {canManage && (adding ? (
            <CostEntryForm jobId={jobId} lines={data.lines} people={people.data ?? []} onSaved={() => { setAdding(false); q.refetch() }} />
          ) : (
            <Button variant="secondary" size="md" needsNetwork onClick={() => setAdding(true)}>Add labour or expense</Button>
          ))}
        </div>
      ) : (
        <div role="status" style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>
          <p>No job costing data was returned.</p>
          <Button size="sm" variant="secondary" onClick={q.refetch}>Retry job costing</Button>
        </div>
      )}
    </section>
  )
}
